/**
 * What a team's admins may change in its Settings view (server/teamSettings.ts),
 * parsed here so the server and the page agree on what a valid value is.
 */
import { VIEWS, type SwitcherView } from "./settings.js";

/**
 * The switcher views a team may switch off. Never the board, which is where a
 * view that is not offered lands, nor Settings itself, or the last admin could
 * lock everyone out of switching anything back on.
 */
export const HIDEABLE_VIEWS = VIEWS.filter((v) => v !== "board" && v !== "team") as Exclude<SwitcherView, "board" | "team">[];
export type HideableView = (typeof HIDEABLE_VIEWS)[number];

/** A list of hidden views, as stored or as sent: unknown names are dropped, never an error. */
export function parseHiddenViews(raw: unknown): HideableView[] {
  const list = typeof raw === "string" ? safeJson(raw) : raw;
  if (!Array.isArray(list)) return [];
  return HIDEABLE_VIEWS.filter((v) => list.includes(v));
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** How long an emailed invite to a team stays good. */
export const INVITE_DAYS = 14;

const EMAIL = /^[^\s<>@,;"]+@[^\s<>@,;"]+\.[^\s<>@,;"]+$/;

/** An address an invite may go to, trimmed and lower-cased, or null. */
export function inviteEmail(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const email = raw.trim().toLowerCase();
  return email.length <= 254 && EMAIL.test(email) ? email : null;
}
